import { useRef, useState, forwardRef, useImperativeHandle } from 'react';
import GlobeOracle, { GlobeOracleHandle, LocationData } from '../../../components/map/GlobeOracle';
import { LocationNamingCard } from './LocationNamingCard';

interface Location {
  lat: number;
  lon: number;
  name: string;
}

export interface LocationMapHandle {
  flyToLocation: (coordinates: [number, number]) => void;
}

interface LocationMapProps {
  initialLocation?: Location;
  favoriteLocations?: Location[];
  onLocationSelect: (location: Location) => void;
  className?: string;
}

export const LocationMap = forwardRef<LocationMapHandle, LocationMapProps>(
  ({ initialLocation, favoriteLocations = [], onLocationSelect, className = '' }, ref) => {
    const mapRef = useRef<GlobeOracleHandle>(null);
    const [selectedLocation, setSelectedLocation] = useState<LocationData | null>(null);
    const [showNamingCard, setShowNamingCard] = useState(false);
    const [clickedCoordinates, setClickedCoordinates] = useState<[number, number] | null>(null);

    // Expose flyToLocation method to parent
    useImperativeHandle(ref, () => ({
      flyToLocation: (coordinates: [number, number]) => {
        if (mapRef.current) {
          mapRef.current.flyToLocation(coordinates);
        }
      },
    }));

    // Convert our Location interface to LocationData interface
    const initialLocationData: LocationData | undefined = initialLocation
      ? {
          name: initialLocation.name,
          coordinates: [initialLocation.lon, initialLocation.lat] as [number, number],
          zoom: 6,
        }
      : undefined;

    // Convert favorite locations to LocationData format for the map
    const favoriteLocationData: LocationData[] = favoriteLocations.map((loc) => ({
      name: loc.name,
      coordinates: [loc.lon, loc.lat] as [number, number],
    }));

    // Include current location in the map if it exists
    const allLocations: LocationData[] = [
      ...favoriteLocationData,
      ...(initialLocation
        ? [
            {
              name: initialLocation.name,
              coordinates: [initialLocation.lon, initialLocation.lat] as [number, number],
            },
          ]
        : []),
    ];

    const handleLocationChange = (locationData: LocationData) => {
      setSelectedLocation(locationData);

      // Convert back to our Location interface
      const location: Location = {
        lat: locationData.coordinates[1],
        lon: locationData.coordinates[0],
        name: locationData.name,
      };

      onLocationSelect(location);
    };

    const handleMapClick = (coordinates: [number, number]) => {
      setClickedCoordinates(coordinates);
      setShowNamingCard(true);
    };

    const handleSubmit = (name: string) => {
      if (clickedCoordinates) {
        const location: Location = {
          lat: clickedCoordinates[1],
          lon: clickedCoordinates[0],
          name,
        };
        onLocationSelect(location);
        setShowNamingCard(false);
        setClickedCoordinates(null);
      }
    };

    const handleCancel = () => {
      setShowNamingCard(false);
      setClickedCoordinates(null);
    };

    return (
      <div className={`relative ${className}`}>
        <GlobeOracle
          ref={mapRef}
          initialLocation={initialLocationData?.coordinates}
          initialZoom={initialLocationData?.zoom || 2}
          targetLocation={selectedLocation?.coordinates}
          locations={allLocations}
          onLocationChange={handleLocationChange}
          onMapClick={handleMapClick}
          isVisible={true}
        />

        {/* Location Naming Card */}
        {showNamingCard && clickedCoordinates && (
          <LocationNamingCard
            coordinates={clickedCoordinates}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
          />
        )}
      </div>
    );
  },
);
